import { throwIfAborted } from '../codecs/types';
import { layoutPortableText } from '../workers/portable-text-client';
import type { PortableTextLayoutInput } from './portable-text-layout';
import { capturePortableTextSnapshotInput, snapshotFromFreshPortableLayout, assertPortableTextSnapshotHeader, type AdmittedPortableTextSnapshot } from './portable-text-snapshot-contract';
export * from './portable-text-snapshot-contract';

/** Snapshot only a freshly computed layout; caller-provided layout bytes are never trusted. */
export async function createPortableTextSnapshot(input: PortableTextLayoutInput, signal?: AbortSignal): Promise<AdmittedPortableTextSnapshot> {
  throwIfAborted(signal);
  const captured = capturePortableTextSnapshotInput(input);
  const layout = await layoutPortableText(captured, signal);
  return snapshotFromFreshPortableLayout(captured, layout, signal);
}

/** Admission binds storage to trusted current document inputs and exact asset bytes.
 * Stored coordinates/plans are never parsed for rendering. A fresh worker result
 * is returned only after the entire stored envelope matches recomputation.
 */
export async function admitPortableTextSnapshot(serialized: string, currentInput: PortableTextLayoutInput, signal?: AbortSignal): Promise<AdmittedPortableTextSnapshot> {
  throwIfAborted(signal);
  assertPortableTextSnapshotHeader(serialized);
  // Strict byte agreement below rejects duplicate keys, extra fields, noncanonical
  // serialization and changed source/settings/fonts/layout, including hash-only tampering.
  const admitted = await createPortableTextSnapshot(currentInput, signal);
  throwIfAborted(signal);
  if (serialized !== admitted.serialized) throw new Error('Portable text snapshot does not match the current source, settings, fonts and recomputed layout.');
  return admitted;
}
